import { useState, useEffect, useRef } from 'react';
import useSwipeTabs from '../hooks/useSwipeTabs';
import ReactDOM from 'react-dom';
import {
    Calendar, Film, Megaphone, BarChart3, Plus, Loader2, Share2,
    Image, Images, CirclePlay, MapPin, CalendarDays, Tag,
    ChevronLeft, ChevronRight, Clock, CheckCircle2, XCircle, AlertCircle,
    Star, MessageSquare, Send, SkipForward, Edit3, X, Trash2,
    Facebook, Instagram, Globe, Eye, MousePointerClick, Users, TrendingUp,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { muze } from '../lib/moduly';
import TabBar from '../components/layout/TabBar';
import NastaveniModulu, { NastaveniTlacitko } from '../components/layout/NastaveniModulu';
import NapojeneUcty from '../components/social/NapojeneUcty';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { SKLO_SVETLE } from '../lib/uiTokens';

// ═══════════════════════════════════════════════════════════
// ── KONSTANTY ─────────────────────────────────────────────

const SECTION_COLOR = '#10B981';
const SECTION_BG    = 'rgba(16,185,129,0.12)';

const ALL_TABS = [
    { id: 'planovac',  label: 'Plánovač',  Icon: Calendar },
    { id: 'generator', label: 'Generátor', Icon: Film },
    { id: 'kampane',   label: 'Kampaně',   Icon: Megaphone },
    { id: 'analytika', label: 'Analytika', Icon: BarChart3 },
];

const PLATFORMA_CFG = {
    fb:  { label: 'Facebook',  color: '#1877F2', bg: 'rgba(24,119,242,0.15)',  short: 'FB',  Icon: Facebook },
    ig:  { label: 'Instagram', color: '#E1306C', bg: 'rgba(225,48,108,0.15)', short: 'IG',  Icon: Instagram },
    gbp: { label: 'Google',    color: '#34A853', bg: 'rgba(52,168,83,0.15)',  short: 'GBP', Icon: Globe },
};

const TYP_CFG = {
    post:      { label: 'Post',         Icon: Image },
    carousel:  { label: 'Carousel',     Icon: Images },
    reels:     { label: 'Reels',        Icon: Film },
    story:     { label: 'Story',        Icon: CirclePlay },
    gbp_post:  { label: 'GBP Post',     Icon: MapPin },
    gbp_event: { label: 'GBP Akce',     Icon: CalendarDays },
    gbp_offer: { label: 'GBP Nabídka',  Icon: Tag },
};

const STAV_CFG = {
    draft:       { label: 'Koncept',     color: '#6b7280', bg: 'rgba(107,114,128,0.12)', border: 'rgba(107,114,128,0.25)', Icon: AlertCircle },
    naplanovano: { label: 'Naplánováno', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.25)',  Icon: Clock },
    publikovano: { label: 'Publikováno', color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.25)',   Icon: CheckCircle2 },
    chyba:       { label: 'Chyba',       color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.25)',   Icon: XCircle },
    archivovano: { label: 'Archiv',      color: '#6b7280', bg: 'rgba(107,114,128,0.12)', border: 'rgba(107,114,128,0.25)', Icon: AlertCircle },
};

const KAMPAN_STAV_CFG = {
    planovana: { label: 'Plánovaná', color: '#6b7280' },
    aktivni:   { label: 'Aktivní',   color: '#22c55e' },
    ukoncena:  { label: 'Ukončená',  color: '#f59e0b' },
    archiv:    { label: 'Archiv',    color: '#6b7280' },
};

const formatDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d)) return '—';
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
};

const formatDateTime = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d)) return '—';
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}. ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

// ═══════════════════════════════════════════════════════════
// ── HLAVNÍ KOMPONENTA ─────────────────────────────────────

export default function Social({ user, onNavigate = null }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    // Záložky = podstránky modulu (social.planovac, …); tvořit a měnit smí, kdo záložku upravuje.
    const tabs = ALL_TABS.filter(t => muze(user, `social.${t.id}`));
    const upravujePlanovac = muze(user, 'social.planovac', 'upravuje');
    // ⚙ Social Media — stav napojených účtů (hodnoty jen ve Správě → Integrace)
    const [nastaveni, setNastaveni] = useState(false);

    const [activeTab, setActiveTab] = useState(() => tabs[0]?.id ?? 'planovac');
    const swipeProps = useSwipeTabs(tabs.map(t => t.id), activeTab, setActiveTab);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Plánovač state
    const [prispevky, setPrispevky] = useState([]);
    const [kampane, setKampane] = useState([]);
    const [loading, setLoading] = useState(true);
    const [calMonth, setCalMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
    });

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editPrispevek, setEditPrispevek] = useState(null);

    // ── Theme colors ──
    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
    const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';

    // ── Data fetch ──
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [prRes, kamRes] = await Promise.all([
                    fetch(`/api/social/prispevky?mesic=${calMonth}`).then(r => r.json()),
                    fetch('/api/social/kampane').then(r => r.json()),
                ]);
                setPrispevky(Array.isArray(prRes) ? prRes : []);
                setKampane(Array.isArray(kamRes) ? kamRes : []);
            } catch (e) {
                console.error('Social fetch error:', e);
            }
            setLoading(false);
        };
        fetchData();
    }, [calMonth]);

    // ── Helpers ──
    const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const deletePrispevek = async (id) => {
        if (!confirm('Opravdu smazat tento příspěvek?')) return;
        const resp = await fetch(`/api/social/prispevky/${id}`, {
            method: 'DELETE',
            headers: { 'X-CSRF-TOKEN': csrfToken() },
        });
        if (resp.ok) setPrispevky(prev => prev.filter(p => p.id !== id));
    };

    // ── Stat counts ──
    const statNaplanovano = prispevky.filter(p => p.stav === 'naplanovano').length;
    const statPublikovano = prispevky.filter(p => p.stav === 'publikovano').length;
    const statDraft       = prispevky.filter(p => p.stav === 'draft').length;
    const statChyba       = prispevky.filter(p => p.stav === 'chyba').length;

    // ── Calendar helpers ──
    const calYear  = parseInt(calMonth.split('-')[0]);
    const calMon   = parseInt(calMonth.split('-')[1]) - 1;
    const daysInMonth = new Date(calYear, calMon + 1, 0).getDate();
    const firstDow = (new Date(calYear, calMon, 1).getDay() + 6) % 7; // Monday = 0
    const calDays  = [];
    for (let i = 0; i < firstDow; i++) calDays.push(null);
    for (let d = 1; d <= daysInMonth; d++) calDays.push(d);

    const prispevkyByDay = {};
    prispevky.forEach(p => {
        const dt = p.publikovat_at || p.publikovano_at || p.created_at;
        if (!dt) return;
        const d = new Date(dt);
        if (d.getFullYear() === calYear && d.getMonth() === calMon) {
            const day = d.getDate();
            if (!prispevkyByDay[day]) prispevkyByDay[day] = [];
            prispevkyByDay[day].push(p);
        }
    });

    const prevMonth = () => {
        const d = new Date(calYear, calMon - 1, 1);
        setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
    };
    const nextMonth = () => {
        const d = new Date(calYear, calMon + 1, 1);
        setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
    };

    const monthNames = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec'];

    if (loading) {
        return <div className="flex items-center justify-center" style={{ minHeight: 300, color: mutedColor }}>
            <Loader2 size={24} className="animate-spin" />
        </div>;
    }

    // ═══════════════════════════════════════════════════════════
    // ── RENDER ─────────────────────────────────────────────────

    return (
        <div>
            {/* ── Header — na mobilu se zobrazuje v TopBaru, tady skryjeme ── */}
            <div className="flex items-center justify-between mb-6">
                {!isMobile && (
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center shrink-0"
                            style={{ width: 40, height: 40, borderRadius: 7, background: SECTION_BG }}>
                            <Share2 size={20} style={{ color: SECTION_COLOR }} />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Social Media</h1>
                            <p className="text-xs" style={{ color: secondaryColor }}>
                                {statNaplanovano} naplánováno · {statPublikovano} publikováno · {statDraft} konceptů
                            </p>
                        </div>
                    </div>
                )}
                <div className="flex items-center gap-2" style={{ marginLeft: 'auto' }}>
                    {upravujePlanovac && (
                        <button onClick={() => { setEditPrispevek(null); setShowModal(true); }}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
                            style={{ background: SECTION_COLOR, color: '#fff' }}>
                            <Plus size={16} /> Nový příspěvek
                        </button>
                    )}
                    {!isMobile && upravujePlanovac && (
                        <NastaveniTlacitko onClick={() => setNastaveni(true)} barva={SECTION_COLOR} isDark={isDark} popis="Napojené účty" />
                    )}
                </div>
            </div>
            {nastaveni && (
                <NastaveniModulu nadpis="Nastavení Social Media" barva={SECTION_COLOR} isDark={isDark} onClose={() => setNastaveni(false)}
                    sekce={[{ id: 'ucty', nazev: 'Napojené účty', popis: 'Meta, Google, AI', obsah: () => (
                        <NapojeneUcty isDark={isDark} onOtevritSpravu={muze(user, 'sprava.integrace') && onNavigate ? () => { setNastaveni(false); onNavigate('sprava', { tab: 'integrace' }); } : null} />
                    ) }]} />
            )}

            {/* Záložky z katalogu — na mobilu je přepíná střed lišty. */}
            <div style={{ marginBottom: isMobile ? 0 : 24 }}>
                <TabBar modul="social" user={user} aktivni={activeTab} onZmena={setActiveTab}
                    barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
            </div>

            <div {...swipeProps}>

            {/* ═══════ TAB: Plánovač ═══════ */}
            {activeTab === 'planovac' && (
                <div className="space-y-5">
                    {/* Stats row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="rounded-xl p-3" style={{ background: STAV_CFG.naplanovano.bg, border: `1px solid ${STAV_CFG.naplanovano.border}` }}>
                            <p className="text-xs" style={{ color: STAV_CFG.naplanovano.color }}>Naplánováno</p>
                            <p className="text-xl font-bold" style={{ color: STAV_CFG.naplanovano.color }}>{statNaplanovano}</p>
                        </div>
                        <div className="rounded-xl p-3" style={{ background: STAV_CFG.publikovano.bg, border: `1px solid ${STAV_CFG.publikovano.border}` }}>
                            <p className="text-xs" style={{ color: STAV_CFG.publikovano.color }}>Publikováno</p>
                            <p className="text-xl font-bold" style={{ color: STAV_CFG.publikovano.color }}>{statPublikovano}</p>
                        </div>
                        <div className="rounded-xl p-3" style={cardStyle}>
                            <p className="text-xs" style={{ color: secondaryColor }}>Koncepty</p>
                            <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{statDraft}</p>
                        </div>
                        <div className="rounded-xl p-3" style={statChyba > 0
                            ? { background: STAV_CFG.chyba.bg, border: `1px solid ${STAV_CFG.chyba.border}` }
                            : cardStyle}>
                            <p className="text-xs" style={{ color: statChyba > 0 ? STAV_CFG.chyba.color : secondaryColor }}>Chyby</p>
                            <p className="text-xl font-bold" style={{ color: statChyba > 0 ? STAV_CFG.chyba.color : 'var(--text-primary)' }}>{statChyba}</p>
                        </div>
                    </div>

                    {/* Aktivní kampaně strip */}
                    {kampane.filter(k => k.stav === 'aktivni').length > 0 && (
                        <div>
                            <h2 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Aktivní kampaně</h2>
                            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                                {kampane.filter(k => k.stav === 'aktivni').map(k => (
                                    <div key={k.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg shrink-0 text-xs font-medium"
                                        style={{ background: k.barva + '20', border: `1px solid ${k.barva}40`, color: k.barva }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: k.barva }} />
                                        {k.nazev}
                                        <span style={{ opacity: 0.6 }}>{k.prispevky_count || 0}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Content Calendar + Nadcházející */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        {/* Calendar */}
                        <div className="lg:col-span-2 rounded-xl p-4" style={cardStyle}>
                            <div className="flex items-center justify-between mb-4">
                                <button onClick={prevMonth} className="p-1 rounded-lg transition-colors hover:opacity-70"
                                    style={{ color: secondaryColor, background: 'none', border: 'none', cursor: 'pointer' }}>
                                    <ChevronLeft size={18} />
                                </button>
                                <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                                    {monthNames[calMon]} {calYear}
                                </h3>
                                <button onClick={nextMonth} className="p-1 rounded-lg transition-colors hover:opacity-70"
                                    style={{ color: secondaryColor, background: 'none', border: 'none', cursor: 'pointer' }}>
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                            {/* Weekday headers */}
                            <div className="grid grid-cols-7 gap-1 mb-1">
                                {['Po','Út','St','Čt','Pá','So','Ne'].map(d => (
                                    <div key={d} className="text-center text-xs font-medium py-1" style={{ color: mutedColor }}>{d}</div>
                                ))}
                            </div>
                            {/* Day cells */}
                            <div className="grid grid-cols-7 gap-1">
                                {calDays.map((day, i) => {
                                    if (day === null) return <div key={`e-${i}`} />;
                                    const events = prispevkyByDay[day] || [];
                                    const today = new Date();
                                    const isToday = day === today.getDate() && calMon === today.getMonth() && calYear === today.getFullYear();
                                    return (
                                        <div key={day} className="rounded-lg p-1 min-h-[56px] cursor-default"
                                            style={{
                                                background: isToday ? SECTION_BG : 'transparent',
                                                border: isToday ? `1px solid ${SECTION_COLOR}40` : `1px solid transparent`,
                                            }}>
                                            <div className="text-xs font-medium mb-0.5" style={{
                                                color: isToday ? SECTION_COLOR : secondaryColor,
                                                textAlign: 'right', paddingRight: 2,
                                            }}>{day}</div>
                                            {events.slice(0, 2).map(ev => {
                                                const typCfg = TYP_CFG[ev.typ_obsahu] || TYP_CFG.post;
                                                const platforma = (ev.platformy || [])[0];
                                                const platCfg = platforma ? PLATFORMA_CFG[platforma] : null;
                                                return (
                                                    <div key={ev.id}
                                                        className="text-xs truncate px-1 py-0.5 rounded mb-0.5 cursor-pointer"
                                                        onClick={() => { setEditPrispevek(ev); setShowModal(true); }}
                                                        style={{
                                                            background: platCfg ? platCfg.bg : SECTION_BG,
                                                            color: platCfg ? platCfg.color : SECTION_COLOR,
                                                            borderLeft: ev.kampan ? `2px solid ${ev.kampan.barva}` : 'none',
                                                            fontSize: 10, lineHeight: '14px',
                                                        }}>
                                                        <typCfg.Icon size={9} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 2 }} />
                                                        {ev.nadpis || ev.caption?.slice(0, 20) || 'Bez názvu'}
                                                    </div>
                                                );
                                            })}
                                            {events.length > 2 && (
                                                <div className="text-xs text-center" style={{ color: mutedColor, fontSize: 9 }}>
                                                    +{events.length - 2}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Nadcházející příspěvky */}
                        <div className="space-y-3">
                            <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Nadcházející</h2>
                            {prispevky
                                .filter(p => p.stav === 'naplanovano' || p.stav === 'draft')
                                .sort((a, b) => new Date(a.publikovat_at || a.created_at) - new Date(b.publikovat_at || b.created_at))
                                .slice(0, 8)
                                .map(p => {
                                    const stav = STAV_CFG[p.stav] || STAV_CFG.draft;
                                    const typCfg = TYP_CFG[p.typ_obsahu] || TYP_CFG.post;
                                    return (
                                        <div key={p.id} className="rounded-xl p-3 cursor-pointer transition-all"
                                            onClick={() => { setEditPrispevek(p); setShowModal(true); }}
                                            onMouseEnter={e => e.currentTarget.style.borderColor = SECTION_COLOR}
                                            onMouseLeave={e => e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}
                                            style={cardStyle}>
                                            <div className="flex items-start gap-2.5">
                                                <div className="flex items-center justify-center shrink-0"
                                                    style={{ width: 32, height: 32, borderRadius: 6, background: SECTION_BG }}>
                                                    <typCfg.Icon size={16} style={{ color: SECTION_COLOR }} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                                                        {p.nadpis || p.caption?.slice(0, 40) || 'Bez názvu'}
                                                    </p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        {(p.platformy || []).map(pl => {
                                                            const cfg = PLATFORMA_CFG[pl];
                                                            return cfg ? (
                                                                <span key={pl} className="text-xs font-medium px-1.5 py-0.5 rounded"
                                                                    style={{ background: cfg.bg, color: cfg.color }}>
                                                                    {cfg.short}
                                                                </span>
                                                            ) : null;
                                                        })}
                                                        <span className="text-xs" style={{ color: stav.color }}>
                                                            {stav.label}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="text-xs" style={{ color: mutedColor }}>
                                                        {formatDateTime(p.publikovat_at || p.created_at)}
                                                    </p>
                                                    {p.kampan && (
                                                        <div className="flex items-center gap-1 mt-1 justify-end">
                                                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: p.kampan.barva }} />
                                                            <span className="text-xs" style={{ color: mutedColor }}>{p.kampan.nazev}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            }
                            {prispevky.filter(p => p.stav === 'naplanovano' || p.stav === 'draft').length === 0 && (
                                <div className="text-center py-8 text-sm" style={{ color: mutedColor }}>
                                    Žádné nadcházející příspěvky
                                </div>
                            )}

                            {/* Recenze inbox */}
                            <RecenzeInbox
                                isAdmin={upravujePlanovac}
                                isDark={isDark}
                                cardStyle={cardStyle}
                                borderColor={borderColor}
                                secondaryColor={secondaryColor}
                                mutedColor={mutedColor}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════ TAB: Generátor ═══════ */}
            {activeTab === 'generator' && (
                <GeneratorTab
                    isAdmin={muze(user, 'social.generator', 'upravuje')}
                    isDark={isDark}
                    cardStyle={cardStyle}
                    borderColor={borderColor}
                    secondaryColor={secondaryColor}
                    mutedColor={mutedColor}
                />
            )}

            {/* ═══════ TAB: Kampaně ═══════ */}
            {activeTab === 'kampane' && (
                <KampaneTab
                    kampane={kampane}
                    setKampane={setKampane}
                    isAdmin={muze(user, 'social.kampane', 'upravuje')}
                    isDark={isDark}
                    cardStyle={cardStyle}
                    borderColor={borderColor}
                    secondaryColor={secondaryColor}
                    mutedColor={mutedColor}
                />
            )}

            {/* ═══════ TAB: Analytika ═══════ */}
            {activeTab === 'analytika' && (
                <AnalytikaTab
                    isDark={isDark}
                    cardStyle={cardStyle}
                    borderColor={borderColor}
                    secondaryColor={secondaryColor}
                    mutedColor={mutedColor}
                />
            )}

            </div>

            {/* ═══════ MODAL: Nový/edit příspěvek ═══════ */}
            {showModal && ReactDOM.createPortal(
                <PrispevekModal
                    prispevek={editPrispevek}
                    kampane={kampane}
                    isDark={isDark}
                    cardStyle={cardStyle}
                    borderColor={borderColor}
                    secondaryColor={secondaryColor}
                    mutedColor={mutedColor}
                    onClose={() => { setShowModal(false); setEditPrispevek(null); }}
                    onSaved={(saved) => {
                        if (editPrispevek) {
                            setPrispevky(prev => prev.map(p => p.id === saved.id ? saved : p));
                        } else {
                            setPrispevky(prev => [saved, ...prev]);
                        }
                        setShowModal(false);
                        setEditPrispevek(null);
                    }}
                    onDelete={(id) => {
                        setPrispevky(prev => prev.filter(p => p.id !== id));
                        setShowModal(false);
                        setEditPrispevek(null);
                    }}
                />,
                document.body
            )}
        </div>
    );
}


// ═══════════════════════════════════════════════════════════
// ── PŘÍSPĚVEK MODAL ───────────────────────────────────────

function PrispevekModal({ prispevek, kampane, isDark, cardStyle, borderColor, secondaryColor, mutedColor, onClose, onSaved, onDelete }) {
    const isEdit = !!prispevek;
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        nadpis:       prispevek?.nadpis || '',
        caption:      prispevek?.caption || '',
        typ_obsahu:   prispevek?.typ_obsahu || 'post',
        platformy:    prispevek?.platformy || ['ig'],
        publikovat_at: prispevek?.publikovat_at ? prispevek.publikovat_at.slice(0, 16) : '',
        kampan_id:    prispevek?.kampan_id || '',
        gbp_titulek:  prispevek?.gbp_titulek || '',
        gbp_cta_typ:  prispevek?.gbp_cta_typ || '',
        gbp_cta_url:  prispevek?.gbp_cta_url || '',
        gbp_event_od: prispevek?.gbp_event_od || '',
        gbp_event_do: prispevek?.gbp_event_do || '',
        gbp_kupon:    prispevek?.gbp_kupon || '',
    });

    const isGbp = form.typ_obsahu.startsWith('gbp_');
    const isStory = form.typ_obsahu === 'story';
    const captionHasPhone = form.platformy.includes('gbp') && /\d{9,}/.test(form.caption);

    const togglePlatforma = (pl) => {
        setForm(prev => ({
            ...prev,
            platformy: prev.platformy.includes(pl)
                ? prev.platformy.filter(p => p !== pl)
                : [...prev.platformy, pl],
        }));
    };

    const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const handleSave = async (stav) => {
        if (saving) return;
        setSaving(true);
        const body = { ...form, kampan_id: form.kampan_id || null };
        if (stav === 'naplanovano' && !form.publikovat_at) {
            body.publikovat_at = new Date().toISOString();
        }
        if (stav) body.stav = stav;

        try {
            const resp = await fetch(
                isEdit ? `/api/social/prispevky/${prispevek.id}` : '/api/social/prispevky',
                {
                    method: isEdit ? 'PUT' : 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken() },
                    body: JSON.stringify(body),
                }
            );
            if (resp.ok) {
                const saved = await resp.json();
                onSaved(saved);
            }
        } catch (e) {
            console.error('Save error:', e);
        }
        setSaving(false);
    };

    const handleDelete = async () => {
        if (!confirm('Opravdu smazat tento příspěvek?')) return;
        const resp = await fetch(`/api/social/prispevky/${prispevek.id}`, {
            method: 'DELETE',
            headers: { 'X-CSRF-TOKEN': csrfToken() },
        });
        if (resp.ok) onDelete(prispevek.id);
    };

    const [publishing, setPublishing] = useState(false);

    const handlePublish = async () => {
        if (!isEdit || publishing) return;
        setPublishing(true);
        try {
            const resp = await fetch(`/api/social/prispevky/${prispevek.id}/publikovat`, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': csrfToken() },
            });
            if (resp.ok) {
                const saved = await resp.json();
                onSaved(saved);
            }
        } catch (e) {
            console.error('Publish error:', e);
        }
        setPublishing(false);
    };

    const inputStyle = {
        ...cardStyle,
        color: 'var(--text-primary)',
        outline: 'none',
        width: '100%',
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.4)' }}
            onClick={onClose}>
            <div className="rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
                style={{
                    ...cardStyle,
                    background: isDark ? 'rgba(28,28,32,0.97)' : 'var(--surface-raised)',
                    backdropFilter: 'blur(40px)',
                    WebkitBackdropFilter: 'blur(40px)',
                    padding: 24,
                }}
                onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                        {isEdit ? 'Upravit příspěvek' : 'Nový příspěvek'}
                    </h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                        <X size={18} />
                    </button>
                </div>

                {/* Typ obsahu */}
                <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>Typ obsahu</label>
                <select value={form.typ_obsahu} onChange={e => setForm(prev => ({ ...prev, typ_obsahu: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 text-sm mb-4" style={inputStyle}>
                    {Object.entries(TYP_CFG).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                    ))}
                </select>

                {/* Platformy */}
                <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>Platformy</label>
                <div className="flex gap-2 mb-4">
                    {Object.entries(PLATFORMA_CFG).map(([key, cfg]) => {
                        const active = form.platformy.includes(key);
                        const disabled = (isGbp && key !== 'gbp') || (!isGbp && key === 'gbp' && !form.typ_obsahu.startsWith('gbp'));
                        return (
                            <button key={key}
                                disabled={disabled}
                                onClick={() => togglePlatforma(key)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                                style={{
                                    background: active ? cfg.bg : 'transparent',
                                    color: active ? cfg.color : mutedColor,
                                    border: `1px solid ${active ? cfg.color + '50' : borderColor}`,
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: disabled ? 0.3 : 1,
                                }}>
                                <cfg.Icon size={13} />
                                {cfg.short}
                            </button>
                        );
                    })}
                </div>

                {/* Nadpis */}
                <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>Nadpis</label>
                <input type="text" value={form.nadpis} onChange={e => setForm(prev => ({ ...prev, nadpis: e.target.value }))}
                    placeholder="Nadpis příspěvku..."
                    className="rounded-lg px-3 py-2 text-sm mb-4" style={inputStyle} />

                {/* Caption */}
                <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>
                    Text příspěvku
                    {form.platformy.includes('gbp') && (
                        <span style={{ color: mutedColor, fontWeight: 400 }}> · max 1500 znaků ({form.caption.length}/1500)</span>
                    )}
                </label>
                <textarea value={form.caption} onChange={e => setForm(prev => ({ ...prev, caption: e.target.value }))}
                    placeholder="Text příspěvku..."
                    rows={4}
                    className="rounded-lg px-3 py-2 text-sm mb-1 resize-none" style={inputStyle} />
                {captionHasPhone && (
                    <p className="text-xs mb-3" style={{ color: '#ef4444' }}>
                        Google Business odmítne post s telefonním číslem — odstraňte ho z textu.
                    </p>
                )}
                {!captionHasPhone && <div className="mb-3" />}

                {/* GBP specifická pole */}
                {isGbp && (
                    <div className="space-y-3 mb-4 p-3 rounded-lg" style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'var(--surface-elevated)' }}>
                        <p className="text-xs font-semibold" style={{ color: PLATFORMA_CFG.gbp.color }}>Google Business pole</p>
                        {form.typ_obsahu === 'gbp_event' && (
                            <>
                                <input type="text" value={form.gbp_titulek} onChange={e => setForm(prev => ({ ...prev, gbp_titulek: e.target.value }))}
                                    placeholder="Název akce" className="rounded-lg px-3 py-2 text-sm w-full" style={inputStyle} />
                                <div className="grid grid-cols-2 gap-2">
                                    <input type="date" value={form.gbp_event_od} onChange={e => setForm(prev => ({ ...prev, gbp_event_od: e.target.value }))}
                                        className="rounded-lg px-3 py-2 text-sm" style={inputStyle} />
                                    <input type="date" value={form.gbp_event_do} onChange={e => setForm(prev => ({ ...prev, gbp_event_do: e.target.value }))}
                                        className="rounded-lg px-3 py-2 text-sm" style={inputStyle} />
                                </div>
                            </>
                        )}
                        {form.typ_obsahu === 'gbp_offer' && (
                            <input type="text" value={form.gbp_kupon} onChange={e => setForm(prev => ({ ...prev, gbp_kupon: e.target.value }))}
                                placeholder="Kód kupónu" className="rounded-lg px-3 py-2 text-sm w-full" style={inputStyle} />
                        )}
                        <div className="grid grid-cols-2 gap-2">
                            <select value={form.gbp_cta_typ} onChange={e => setForm(prev => ({ ...prev, gbp_cta_typ: e.target.value }))}
                                className="rounded-lg px-3 py-2 text-sm" style={inputStyle}>
                                <option value="">Bez CTA</option>
                                <option value="BOOK">Rezervovat</option>
                                <option value="ORDER">Objednat</option>
                                <option value="SHOP">Nakoupit</option>
                                <option value="LEARN_MORE">Zjistit více</option>
                                <option value="SIGN_UP">Registrovat</option>
                                <option value="CALL">Zavolat</option>
                            </select>
                            {form.gbp_cta_typ && (
                                <input type="url" value={form.gbp_cta_url} onChange={e => setForm(prev => ({ ...prev, gbp_cta_url: e.target.value }))}
                                    placeholder="CTA URL" className="rounded-lg px-3 py-2 text-sm" style={inputStyle} />
                            )}
                        </div>
                    </div>
                )}

                {/* Kampaň */}
                <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>Kampaň</label>
                <select value={form.kampan_id} onChange={e => setForm(prev => ({ ...prev, kampan_id: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 text-sm mb-4" style={inputStyle}>
                    <option value="">Bez kampaně</option>
                    {kampane.map(k => (
                        <option key={k.id} value={k.id}>{k.nazev}</option>
                    ))}
                </select>

                {/* Čas publikace */}
                <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>Čas publikace</label>
                <input type="datetime-local" value={form.publikovat_at}
                    onChange={e => setForm(prev => ({ ...prev, publikovat_at: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 text-sm mb-6" style={inputStyle} />

                {/* Akce */}
                <div className="flex items-center gap-2">
                    <button onClick={() => handleSave('draft')} disabled={saving}
                        className="px-4 py-2 rounded-lg text-sm font-medium"
                        style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-elevated)', color: 'var(--text-primary)', border: `1px solid ${borderColor}`, cursor: 'pointer' }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : 'Uložit koncept'}
                    </button>
                    <button onClick={() => handleSave('naplanovano')} disabled={saving}
                        className="px-4 py-2 rounded-lg text-sm font-semibold"
                        style={{ background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer' }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : 'Naplánovat'}
                    </button>
                    {isEdit && prispevek.stav !== 'publikovano' && (
                        <button onClick={handlePublish} disabled={publishing}
                            className="px-4 py-2 rounded-lg text-sm font-semibold"
                            style={{ background: '#f59e0b', color: '#fff', border: 'none', cursor: 'pointer' }}>
                            {publishing ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: '-2px' }} />Publikovat</>}
                        </button>
                    )}
                    {isEdit && prispevek.stav !== 'publikovano' && (
                        <button onClick={handleDelete} className="ml-auto p-2 rounded-lg"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


// ═══════════════════════════════════════════════════════════
// ── GENERÁTOR TAB ─────────────────────────────────────────

const GEN_STAV = {
    ceka:     { label: 'Čeká',       color: '#6b7280', Icon: Clock },
    generuje: { label: 'Generuje…',  color: '#f59e0b', Icon: Loader2 },
    hotovo:   { label: 'Hotovo',     color: '#22c55e', Icon: CheckCircle2 },
    chyba:    { label: 'Chyba',      color: '#ef4444', Icon: XCircle },
};

function GeneratorTab({ isAdmin, isDark, cardStyle, borderColor, secondaryColor, mutedColor }) {
    const [hudbaList, setHudbaList] = useState([]);
    const [sablonyList, setSablonyList] = useState([]);
    const [mediaSoubory, setMediaSoubory] = useState([]);
    const [historie, setHistorie] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    // Form state
    const [selectedFotos, setSelectedFotos] = useState([]);
    const [selectedHudba, setSelectedHudba] = useState('');
    const [selectedSablona, setSelectedSablona] = useState('');
    const [nadpis, setNadpis] = useState('');
    const [podnadpis, setPodnadpis] = useState('');
    const [format, setFormat] = useState('story_9_16');

    // Generation state
    const [generatingJobId, setGeneratingJobId] = useState(null);
    const [generatingStav, setGeneratingStav] = useState(null);
    const [generatingResult, setGeneratingResult] = useState(null);
    const pollingRef = useRef(null);

    const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [hRes, sRes, mRes, gRes] = await Promise.all([
                    fetch('/api/social/hudba').then(r => r.json()),
                    fetch('/api/social/sablony').then(r => r.json()),
                    fetch('/api/media/?slozka=/').then(r => r.json()).catch(() => []),
                    fetch('/api/social/generovani').then(r => r.json()),
                ]);
                setHudbaList(Array.isArray(hRes) ? hRes : []);
                setSablonyList(Array.isArray(sRes) ? sRes : []);
                setMediaSoubory(Array.isArray(mRes) ? mRes.filter(f => f.typ === 'foto') : []);
                setHistorie(Array.isArray(gRes) ? gRes : []);
            } catch (e) {
                console.error('Generator fetch error:', e);
            }
            setLoadingData(false);
        };
        fetchAll();
        return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }, []);

    const startPolling = (jobId) => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = setInterval(async () => {
            try {
                const res = await fetch(`/api/social/generovani/${jobId}/stav`);
                const data = await res.json();
                setGeneratingStav(data.stav);
                if (data.stav === 'hotovo' || data.stav === 'chyba') {
                    clearInterval(pollingRef.current);
                    pollingRef.current = null;
                    setGeneratingResult(data);
                    // Refresh historie
                    fetch('/api/social/generovani').then(r => r.json()).then(g => {
                        if (Array.isArray(g)) setHistorie(g);
                    });
                }
            } catch (e) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        }, 2000);
    };

    const handleGenerate = async () => {
        if (selectedFotos.length === 0) return;
        setGeneratingResult(null);
        setGeneratingStav('ceka');

        try {
            const resp = await fetch('/api/social/generovani', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken() },
                body: JSON.stringify({
                    foto_ids: selectedFotos,
                    hudba_id: selectedHudba || null,
                    sablona_id: selectedSablona || null,
                    nadpis: nadpis || null,
                    podnadpis: podnadpis || null,
                    format,
                }),
            });
            if (resp.ok) {
                const job = await resp.json();
                setGeneratingJobId(job.id);
                setGeneratingStav('ceka');
                startPolling(job.id);
            }
        } catch (e) {
            console.error('Generate error:', e);
            setGeneratingStav(null);
        }
    };

    const toggleFoto = (id) => {
        setSelectedFotos(prev =>
            prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
        );
    };

    if (loadingData) {
        return <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin" style={{ color: mutedColor }} />
        </div>;
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* ── Form + Preview ── */}
            <div className="lg:col-span-2 space-y-4">
                {/* Šablony */}
                {sablonyList.length > 0 && (
                    <div>
                        <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Šablona</h3>
                        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                            <button onClick={() => setSelectedSablona('')}
                                className="shrink-0 rounded-lg px-3 py-2 text-xs font-medium"
                                style={{
                                    background: !selectedSablona ? SECTION_BG : 'transparent',
                                    color: !selectedSablona ? SECTION_COLOR : secondaryColor,
                                    border: `1px solid ${!selectedSablona ? SECTION_COLOR + '40' : borderColor}`,
                                    cursor: 'pointer',
                                }}>
                                Bez šablony
                            </button>
                            {sablonyList.map(s => (
                                <button key={s.id} onClick={() => setSelectedSablona(s.id)}
                                    className="shrink-0 rounded-lg px-3 py-2 text-xs font-medium"
                                    style={{
                                        background: selectedSablona === s.id ? SECTION_BG : 'transparent',
                                        color: selectedSablona === s.id ? SECTION_COLOR : secondaryColor,
                                        border: `1px solid ${selectedSablona === s.id ? SECTION_COLOR + '40' : borderColor}`,
                                        cursor: 'pointer',
                                    }}>
                                    {s.nazev}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Fotky */}
                <div>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                        Fotky <span className="font-normal" style={{ color: mutedColor }}>({selectedFotos.length} vybraných)</span>
                    </h3>
                    {mediaSoubory.length === 0 ? (
                        <div className="rounded-xl p-6 text-center" style={cardStyle}>
                            <Image size={32} style={{ color: mutedColor, margin: '0 auto 8px' }} />
                            <p className="text-xs" style={{ color: mutedColor }}>
                                Nahrajte fotky do mediální knihovny (Správce souborů)
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-[240px] overflow-y-auto rounded-xl p-2" style={cardStyle}>
                            {mediaSoubory.slice(0, 48).map(f => {
                                const sel = selectedFotos.includes(f.id);
                                const idx = selectedFotos.indexOf(f.id);
                                return (
                                    <div key={f.id} onClick={() => toggleFoto(f.id)}
                                        className="relative rounded-lg overflow-hidden cursor-pointer aspect-square"
                                        style={{
                                            border: sel ? `2px solid ${SECTION_COLOR}` : `1px solid ${borderColor}`,
                                            opacity: sel ? 1 : 0.7,
                                        }}>
                                        <img src={`/storage/${f.thumbnail_cesta || f.cesta}`}
                                            alt={f.nazev}
                                            className="w-full h-full object-cover" />
                                        {sel && (
                                            <div className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                                                style={{ background: SECTION_COLOR }}>
                                                {idx + 1}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Nadpis + podnadpis */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs font-medium mb-1 block" style={{ color: secondaryColor }}>Nadpis</label>
                        <input type="text" value={nadpis} onChange={e => setNadpis(e.target.value)}
                            placeholder="Hlavní text na videu..."
                            className="w-full rounded-lg px-3 py-2 text-sm" style={{ ...cardStyle, color: 'var(--text-primary)', outline: 'none' }} />
                    </div>
                    <div>
                        <label className="text-xs font-medium mb-1 block" style={{ color: secondaryColor }}>Podnadpis</label>
                        <input type="text" value={podnadpis} onChange={e => setPodnadpis(e.target.value)}
                            placeholder="Sekundární text..."
                            className="w-full rounded-lg px-3 py-2 text-sm" style={{ ...cardStyle, color: 'var(--text-primary)', outline: 'none' }} />
                    </div>
                </div>

                {/* Hudba */}
                <div>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Hudba</h3>
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={() => setSelectedHudba('')}
                            className="rounded-lg px-3 py-1.5 text-xs font-medium"
                            style={{
                                background: !selectedHudba ? SECTION_BG : 'transparent',
                                color: !selectedHudba ? SECTION_COLOR : secondaryColor,
                                border: `1px solid ${!selectedHudba ? SECTION_COLOR + '40' : borderColor}`,
                                cursor: 'pointer',
                            }}>
                            Bez hudby
                        </button>
                        {hudbaList.map(h => (
                            <button key={h.id} onClick={() => setSelectedHudba(h.id)}
                                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium"
                                style={{
                                    background: selectedHudba === h.id ? SECTION_BG : 'transparent',
                                    color: selectedHudba === h.id ? SECTION_COLOR : secondaryColor,
                                    border: `1px solid ${selectedHudba === h.id ? SECTION_COLOR + '40' : borderColor}`,
                                    cursor: 'pointer',
                                }}>
                                {h.nazev}
                                <span style={{ opacity: 0.5 }}>{Math.floor(h.delka_s / 60)}:{String(h.delka_s % 60).padStart(2, '0')}</span>
                            </button>
                        ))}
                        {hudbaList.length === 0 && (
                            <span className="text-xs" style={{ color: mutedColor }}>Žádná hudba v knihovně</span>
                        )}
                    </div>
                </div>

                {/* Format + Generovat */}
                <div className="flex items-center gap-3">
                    <select value={format} onChange={e => setFormat(e.target.value)}
                        className="rounded-lg px-3 py-2 text-sm"
                        style={{ ...cardStyle, color: 'var(--text-primary)', outline: 'none' }}>
                        <option value="story_9_16">Story 9:16</option>
                        <option value="reels_9_16">Reels 9:16</option>
                    </select>

                    <button onClick={handleGenerate}
                        disabled={selectedFotos.length === 0 || (generatingStav === 'ceka' || generatingStav === 'generuje')}
                        className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-opacity"
                        style={{
                            background: SECTION_COLOR,
                            color: '#fff',
                            border: 'none',
                            cursor: selectedFotos.length === 0 ? 'not-allowed' : 'pointer',
                            opacity: selectedFotos.length === 0 ? 0.4 : 1,
                        }}>
                        {(generatingStav === 'ceka' || generatingStav === 'generuje')
                            ? <><Loader2 size={16} className="animate-spin" /> Generuji…</>
                            : <><Film size={16} /> Generovat MP4</>
                        }
                    </button>
                </div>

                {/* Result */}
                {generatingResult && (
                    <div className="rounded-xl p-4" style={{
                        ...cardStyle,
                        borderColor: generatingResult.stav === 'hotovo' ? '#22c55e40' : '#ef444440',
                    }}>
                        {generatingResult.stav === 'hotovo' ? (
                            <div className="flex items-center gap-3">
                                <CheckCircle2 size={20} style={{ color: '#22c55e' }} />
                                <div className="flex-1">
                                    <p className="text-sm font-medium" style={{ color: '#22c55e' }}>Video vygenerováno</p>
                                    <p className="text-xs" style={{ color: mutedColor }}>{generatingResult.vystup_cesta}</p>
                                </div>
                                <a href={`/storage/${generatingResult.vystup_cesta}`} target="_blank" rel="noopener"
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                                    style={{ background: SECTION_BG, color: SECTION_COLOR, textDecoration: 'none' }}>
                                    Stáhnout
                                </a>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <XCircle size={20} style={{ color: '#ef4444' }} />
                                <div>
                                    <p className="text-sm font-medium" style={{ color: '#ef4444' }}>Generování selhalo</p>
                                    <p className="text-xs" style={{ color: mutedColor }}>{generatingResult.chyba_detail}</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Preview + Historie ── */}
            <div className="space-y-4">
                {/* CSS Preview 9:16 */}
                <div>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Náhled</h3>
                    <div className="rounded-xl overflow-hidden mx-auto" style={{
                        width: 180, height: 320,
                        background: 'linear-gradient(180deg, rgba(16,185,129,0.3) 0%, rgba(16,185,129,0.05) 50%, rgba(0,0,0,0.6) 100%)',
                        border: `1px solid ${borderColor}`,
                        position: 'relative',
                    }}>
                        {/* Preview fotka */}
                        {selectedFotos.length > 0 && mediaSoubory.length > 0 && (() => {
                            const first = mediaSoubory.find(f => f.id === selectedFotos[0]);
                            return first ? (
                                <img src={`/storage/${first.thumbnail_cesta || first.cesta}`}
                                    alt="preview" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.6 }} />
                            ) : null;
                        })()}
                        {/* Text overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-3" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
                            {nadpis && <p className="text-white text-sm font-bold leading-tight mb-0.5">{nadpis}</p>}
                            {podnadpis && <p className="text-white text-xs opacity-80">{podnadpis}</p>}
                            {selectedHudba && (() => {
                                const h = hudbaList.find(h => h.id === selectedHudba);
                                return h ? (
                                    <div className="flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-white" style={{ background: 'rgba(255,255,255,0.15)', fontSize: 9, width: 'fit-content' }}>
                                        ♪ {h.nazev}
                                    </div>
                                ) : null;
                            })()}
                        </div>
                        {/* Slide count */}
                        <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-white text-xs font-bold"
                            style={{ background: 'rgba(0,0,0,0.4)', fontSize: 10 }}>
                            {selectedFotos.length} foto
                        </div>
                        {/* Format label */}
                        <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-xs"
                            style={{ background: SECTION_BG, color: SECTION_COLOR, fontSize: 9, fontWeight: 600 }}>
                            {format === 'story_9_16' ? 'Story' : 'Reels'}
                        </div>
                    </div>
                </div>

                {/* Historie generování */}
                <div>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Historie</h3>
                    {historie.length === 0 ? (
                        <p className="text-xs text-center py-4" style={{ color: mutedColor }}>Zatím žádné generování</p>
                    ) : (
                        <div className="space-y-2">
                            {historie.slice(0, 10).map(g => {
                                const stavCfg = GEN_STAV[g.stav] || GEN_STAV.ceka;
                                return (
                                    <div key={g.id} className="rounded-xl p-3" style={cardStyle}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <stavCfg.Icon size={14} style={{ color: stavCfg.color }}
                                                className={g.stav === 'generuje' ? 'animate-spin' : ''} />
                                            <span className="text-xs font-medium" style={{ color: stavCfg.color }}>{stavCfg.label}</span>
                                            <span className="text-xs ml-auto" style={{ color: mutedColor }}>
                                                {formatDate(g.created_at)}
                                            </span>
                                        </div>
                                        {g.nadpis && <p className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>{g.nadpis}</p>}
                                        <p className="text-xs" style={{ color: mutedColor }}>
                                            {(g.foto_ids || []).length} fotek
                                            {g.hudba ? ` · ${g.hudba.nazev}` : ''}
                                        </p>
                                        {g.stav === 'hotovo' && g.vystup_cesta && (
                                            <a href={`/storage/${g.vystup_cesta}`} target="_blank" rel="noopener"
                                                className="inline-block mt-1 text-xs font-medium"
                                                style={{ color: SECTION_COLOR, textDecoration: 'none' }}>
                                                Stáhnout MP4
                                            </a>
                                        )}
                                        {g.stav === 'chyba' && g.chyba_detail && (
                                            <p className="text-xs mt-1 truncate" style={{ color: '#ef4444' }}>{g.chyba_detail}</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


// ═══════════════════════════════════════════════════════════
// ── KAMPANĚ TAB ───────────────────────────────────────────

function KampaneTab({ kampane, setKampane, isAdmin, isDark, cardStyle, borderColor, secondaryColor, mutedColor }) {
    const [showModal, setShowModal] = useState(false);
    const [editKampan, setEditKampan] = useState(null);

    const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const handleSaved = (saved) => {
        if (editKampan) {
            setKampane(prev => prev.map(k => k.id === saved.id ? saved : k));
        } else {
            setKampane(prev => [saved, ...prev]);
        }
        setShowModal(false);
        setEditKampan(null);
    };

    const handleDelete = async (id) => {
        if (!confirm('Opravdu smazat tuto kampaň?')) return;
        const resp = await fetch(`/api/social/kampane/${id}`, {
            method: 'DELETE',
            headers: { 'X-CSRF-TOKEN': csrfToken() },
        });
        if (resp.ok) {
            setKampane(prev => prev.filter(k => k.id !== id));
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Kampaně <span className="font-normal" style={{ color: mutedColor }}>({kampane.length})</span>
                </h2>
                {isAdmin && (
                    <button onClick={() => { setEditKampan(null); setShowModal(true); }}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold"
                        style={{ background: SECTION_BG, color: SECTION_COLOR, border: 'none', cursor: 'pointer' }}>
                        <Plus size={14} /> Nová kampaň
                    </button>
                )}
            </div>

            {kampane.length === 0 ? (
                <div className="text-center py-16">
                    <Megaphone size={48} style={{ color: mutedColor, margin: '0 auto 12px' }} />
                    <p className="text-sm" style={{ color: mutedColor }}>Zatím žádné kampaně</p>
                    {isAdmin && (
                        <button onClick={() => setShowModal(true)}
                            className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold"
                            style={{ background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer' }}>
                            Vytvořit první kampaň
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {kampane.map(k => {
                        const stavCfg = KAMPAN_STAV_CFG[k.stav] || KAMPAN_STAV_CFG.planovana;
                        return (
                            <div key={k.id} className="rounded-xl p-4 cursor-pointer transition-all"
                                onClick={() => { setEditKampan(k); setShowModal(true); }}
                                onMouseEnter={e => e.currentTarget.style.borderColor = k.barva}
                                onMouseLeave={e => e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}
                                style={{ ...cardStyle, borderLeft: `3px solid ${k.barva}` }}>
                                <div className="flex items-start justify-between mb-2">
                                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{k.nazev}</h3>
                                    <span className="text-xs px-2 py-0.5 rounded-full shrink-0" style={{
                                        background: stavCfg.color + '20', color: stavCfg.color,
                                    }}>{stavCfg.label}</span>
                                </div>
                                {k.popis && <p className="text-xs mb-2 line-clamp-2" style={{ color: secondaryColor }}>{k.popis}</p>}
                                <div className="flex items-center gap-2">
                                    {(k.platformy || []).map(pl => {
                                        const cfg = PLATFORMA_CFG[pl];
                                        return cfg ? (
                                            <span key={pl} className="text-xs px-1.5 py-0.5 rounded" style={{ background: cfg.bg, color: cfg.color }}>
                                                {cfg.short}
                                            </span>
                                        ) : null;
                                    })}
                                    <span className="text-xs" style={{ color: mutedColor }}>
                                        {k.prispevky_count || 0} příspěvků
                                    </span>
                                </div>
                                {(k.datum_od || k.datum_do) && (
                                    <p className="text-xs mt-2" style={{ color: mutedColor }}>
                                        {formatDate(k.datum_od)} — {formatDate(k.datum_do)}
                                    </p>
                                )}
                                {k.opakujici && k.opakujici_popis && (
                                    <p className="text-xs mt-1" style={{ color: SECTION_COLOR }}>↻ {k.opakujici_popis}</p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* KampanModal */}
            {showModal && ReactDOM.createPortal(
                <KampanModal
                    kampan={editKampan}
                    isDark={isDark}
                    cardStyle={cardStyle}
                    borderColor={borderColor}
                    secondaryColor={secondaryColor}
                    mutedColor={mutedColor}
                    onClose={() => { setShowModal(false); setEditKampan(null); }}
                    onSaved={handleSaved}
                    onDelete={handleDelete}
                />,
                document.body
            )}
        </div>
    );
}


// ═══════════════════════════════════════════════════════════
// ── KAMPAŇ MODAL ──────────────────────────────────────────

const BARVY_PRESET = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#0CC0DF', '#6b7280'];

function KampanModal({ kampan, isDark, cardStyle, borderColor, secondaryColor, mutedColor, onClose, onSaved, onDelete }) {
    const isEdit = !!kampan;
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        nazev:           kampan?.nazev || '',
        popis:           kampan?.popis || '',
        barva:           kampan?.barva || '#10B981',
        datum_od:        kampan?.datum_od || '',
        datum_do:        kampan?.datum_do || '',
        opakujici:       kampan?.opakujici || false,
        opakujici_popis: kampan?.opakujici_popis || '',
        stav:            kampan?.stav || 'planovana',
        platformy:       kampan?.platformy || ['fb', 'ig'],
    });

    const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const togglePlatforma = (pl) => {
        setForm(prev => ({
            ...prev,
            platformy: prev.platformy.includes(pl)
                ? prev.platformy.filter(p => p !== pl)
                : [...prev.platformy, pl],
        }));
    };

    const handleSave = async () => {
        if (!form.nazev.trim() || saving) return;
        setSaving(true);
        try {
            const resp = await fetch(
                isEdit ? `/api/social/kampane/${kampan.id}` : '/api/social/kampane',
                {
                    method: isEdit ? 'PUT' : 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken() },
                    body: JSON.stringify(form),
                }
            );
            if (resp.ok) onSaved(await resp.json());
        } catch (e) {
            console.error('Kampan save error:', e);
        }
        setSaving(false);
    };

    const inputStyle = { ...cardStyle, color: 'var(--text-primary)', outline: 'none', width: '100%' };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.4)' }}
            onClick={onClose}>
            <div className="rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto"
                style={{
                    ...cardStyle,
                    background: isDark ? 'rgba(28,28,32,0.97)' : 'var(--surface-raised)',
                    backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
                    padding: 24,
                }}
                onClick={e => e.stopPropagation()}>

                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                        {isEdit ? 'Upravit kampaň' : 'Nová kampaň'}
                    </h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                        <X size={18} />
                    </button>
                </div>

                {/* Název */}
                <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>Název kampaně *</label>
                <input type="text" value={form.nazev} onChange={e => setForm(prev => ({ ...prev, nazev: e.target.value }))}
                    placeholder="Název kampaně..."
                    className="rounded-lg px-3 py-2 text-sm mb-4" style={inputStyle} />

                {/* Popis */}
                <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>Popis</label>
                <textarea value={form.popis} onChange={e => setForm(prev => ({ ...prev, popis: e.target.value }))}
                    placeholder="Volitelný popis..."
                    rows={2} className="rounded-lg px-3 py-2 text-sm mb-4 resize-none" style={inputStyle} />

                {/* Barva */}
                <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>Barva</label>
                <div className="flex gap-2 mb-4">
                    {BARVY_PRESET.map(b => (
                        <button key={b} onClick={() => setForm(prev => ({ ...prev, barva: b }))}
                            className="rounded-full" style={{
                                width: 24, height: 24, background: b, border: form.barva === b ? '2px solid var(--text-primary)' : '2px solid transparent',
                                cursor: 'pointer',
                            }} />
                    ))}
                </div>

                {/* Platformy */}
                <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>Platformy</label>
                <div className="flex gap-2 mb-4">
                    {Object.entries(PLATFORMA_CFG).map(([key, cfg]) => {
                        const active = form.platformy.includes(key);
                        return (
                            <button key={key} onClick={() => togglePlatforma(key)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                                style={{
                                    background: active ? cfg.bg : 'transparent',
                                    color: active ? cfg.color : mutedColor,
                                    border: `1px solid ${active ? cfg.color + '50' : borderColor}`,
                                    cursor: 'pointer',
                                }}>
                                <cfg.Icon size={13} /> {cfg.short}
                            </button>
                        );
                    })}
                </div>

                {/* Datum */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                        <label className="text-xs font-medium mb-1 block" style={{ color: secondaryColor }}>Od</label>
                        <input type="date" value={form.datum_od} onChange={e => setForm(prev => ({ ...prev, datum_od: e.target.value }))}
                            className="rounded-lg px-3 py-2 text-sm w-full" style={inputStyle} />
                    </div>
                    <div>
                        <label className="text-xs font-medium mb-1 block" style={{ color: secondaryColor }}>Do</label>
                        <input type="date" value={form.datum_do} onChange={e => setForm(prev => ({ ...prev, datum_do: e.target.value }))}
                            className="rounded-lg px-3 py-2 text-sm w-full" style={inputStyle} />
                    </div>
                </div>

                {/* Stav */}
                {isEdit && (
                    <>
                        <label className="text-xs font-medium mb-1.5 block" style={{ color: secondaryColor }}>Stav</label>
                        <select value={form.stav} onChange={e => setForm(prev => ({ ...prev, stav: e.target.value }))}
                            className="w-full rounded-lg px-3 py-2 text-sm mb-4" style={inputStyle}>
                            {Object.entries(KAMPAN_STAV_CFG).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                            ))}
                        </select>
                    </>
                )}

                {/* Opakující se */}
                <label className="flex items-center gap-2 mb-4 cursor-pointer">
                    <input type="checkbox" checked={form.opakujici}
                        onChange={e => setForm(prev => ({ ...prev, opakujici: e.target.checked }))}
                        style={{ accentColor: SECTION_COLOR }} />
                    <span className="text-xs font-medium" style={{ color: secondaryColor }}>Opakující se kampaň</span>
                </label>
                {form.opakujici && (
                    <input type="text" value={form.opakujici_popis}
                        onChange={e => setForm(prev => ({ ...prev, opakujici_popis: e.target.value }))}
                        placeholder="např. každý čtvrtek"
                        className="rounded-lg px-3 py-2 text-sm mb-4" style={inputStyle} />
                )}

                {/* Akce */}
                <div className="flex items-center gap-2">
                    <button onClick={handleSave} disabled={saving || !form.nazev.trim()}
                        className="px-4 py-2 rounded-lg text-sm font-semibold"
                        style={{ background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer', opacity: !form.nazev.trim() ? 0.4 : 1 }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : (isEdit ? 'Uložit' : 'Vytvořit')}
                    </button>
                    {isEdit && (
                        <button onClick={() => { onDelete(kampan.id); onClose(); }}
                            className="ml-auto p-2 rounded-lg"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


// ═══════════════════════════════════════════════════════════
// ── ANALYTIKA TAB ─────────────────────────────────────────

const ANALYTIKA_METRIKY = [
    { key: 'dosah',      label: 'Dosah',       color: '#3B82F6' },
    { key: 'imprese',    label: 'Imprese',     color: '#8B5CF6' },
    { key: 'engagement', label: 'Engagement',  color: '#10B981' },
    { key: 'sledujici',  label: 'Sledující',   color: '#F59E0B' },
    { key: 'kliknuti',   label: 'Kliknutí',    color: '#EF4444' },
];

function AnalytikaTab({ isDark, cardStyle, borderColor, secondaryColor, mutedColor }) {
    const [platforma, setPlatforma] = useState('fb');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState(() => {
        const now = new Date();
        const od = new Date(now); od.setDate(od.getDate() - 30);
        return {
            od: od.toISOString().slice(0, 10),
            do: now.toISOString().slice(0, 10),
        };
    });
    const [activeMetric, setActiveMetric] = useState('dosah');

    useEffect(() => {
        setLoading(true);
        fetch(`/api/social/statistiky?platforma=${platforma}&od=${dateRange.od}&do=${dateRange.do}`)
            .then(r => r.json())
            .then(d => {
                setData(Array.isArray(d) ? d.map(s => ({
                    ...s,
                    datum_label: new Date(s.datum).toLocaleDateString('cs', { day: 'numeric', month: 'numeric' }),
                })) : []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [platforma, dateRange]);

    const metric = ANALYTIKA_METRIKY.find(m => m.key === activeMetric) || ANALYTIKA_METRIKY[0];
    const total = data.reduce((sum, d) => sum + (d[activeMetric] || 0), 0);
    const avg = data.length > 0 ? Math.round(total / data.length) : 0;

    return (
        <div className="space-y-4">
            {/* Platform tabs + date range */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex gap-1">
                    {Object.entries(PLATFORMA_CFG).map(([key, cfg]) => (
                        <button key={key} onClick={() => setPlatforma(key)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                            style={{
                                background: platforma === key ? cfg.bg : 'transparent',
                                color: platforma === key ? cfg.color : secondaryColor,
                                border: `1px solid ${platforma === key ? cfg.color + '50' : borderColor}`,
                                cursor: 'pointer',
                            }}>
                            <cfg.Icon size={13} /> {cfg.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <input type="date" value={dateRange.od}
                        onChange={e => setDateRange(prev => ({ ...prev, od: e.target.value }))}
                        className="rounded-lg px-2 py-1 text-xs"
                        style={{ ...cardStyle, color: 'var(--text-primary)', outline: 'none' }} />
                    <span className="text-xs" style={{ color: mutedColor }}>—</span>
                    <input type="date" value={dateRange.do}
                        onChange={e => setDateRange(prev => ({ ...prev, do: e.target.value }))}
                        className="rounded-lg px-2 py-1 text-xs"
                        style={{ ...cardStyle, color: 'var(--text-primary)', outline: 'none' }} />
                </div>
            </div>

            {/* Metric pills */}
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                {ANALYTIKA_METRIKY.map(m => (
                    <button key={m.key} onClick={() => setActiveMetric(m.key)}
                        className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium"
                        style={{
                            background: activeMetric === m.key ? m.color + '20' : 'transparent',
                            color: activeMetric === m.key ? m.color : secondaryColor,
                            border: `1px solid ${activeMetric === m.key ? m.color + '40' : borderColor}`,
                            cursor: 'pointer',
                        }}>
                        {m.label}
                    </button>
                ))}
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl p-3" style={cardStyle}>
                    <p className="text-xs" style={{ color: secondaryColor }}>Celkem</p>
                    <p className="text-xl font-bold" style={{ color: metric.color }}>{total.toLocaleString('cs')}</p>
                </div>
                <div className="rounded-xl p-3" style={cardStyle}>
                    <p className="text-xs" style={{ color: secondaryColor }}>Denní průměr</p>
                    <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{avg.toLocaleString('cs')}</p>
                </div>
                <div className="rounded-xl p-3" style={cardStyle}>
                    <p className="text-xs" style={{ color: secondaryColor }}>Dní</p>
                    <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{data.length}</p>
                </div>
            </div>

            {/* Chart */}
            <div className="rounded-xl p-4" style={cardStyle}>
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 size={24} className="animate-spin" style={{ color: mutedColor }} />
                    </div>
                ) : data.length === 0 ? (
                    <div className="text-center py-12">
                        <BarChart3 size={32} style={{ color: mutedColor, margin: '0 auto 8px' }} />
                        <p className="text-xs" style={{ color: mutedColor }}>Žádná data pro vybrané období</p>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                            <defs>
                                <linearGradient id={`grad-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={metric.color} stopOpacity={0.3} />
                                    <stop offset="100%" stopColor={metric.color} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} />
                            <XAxis dataKey="datum_label" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#999' }} />
                            <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#999' }} width={45} />
                            <Tooltip
                                contentStyle={{
                                    background: isDark ? '#1C1C20' : '#fff',
                                    border: `1px solid ${borderColor}`,
                                    borderRadius: 6, fontSize: 12,
                                    color: 'var(--text-primary)',
                                }}
                            />
                            <Area type="monotone" dataKey={activeMetric} stroke={metric.color} strokeWidth={2}
                                fill={`url(#grad-${metric.key})`} />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}


// ═══════════════════════════════════════════════════════════
// ── RECENZE INBOX ─────────────────────────────────────────

function RecenzeInbox({ isAdmin, isDark, cardStyle, borderColor, secondaryColor, mutedColor }) {
    const [recenze, setRecenze] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editText, setEditText] = useState({});

    const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

    useEffect(() => {
        const fetchRecenze = () => {
            fetch('/api/social/recenze?ai_stav=ceka')
                .then(r => r.json())
                .then(d => { setRecenze(Array.isArray(d) ? d : []); setLoading(false); })
                .catch(() => setLoading(false));
        };
        fetchRecenze();
        const interval = setInterval(fetchRecenze, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleSchval = async (id, text) => {
        const resp = await fetch(`/api/social/recenze/${id}/schval`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken() },
            body: JSON.stringify({ text }),
        });
        if (resp.ok) setRecenze(prev => prev.filter(r => r.id !== id));
    };

    const handlePreskoc = async (id) => {
        const resp = await fetch(`/api/social/recenze/${id}/preskoc`, {
            method: 'POST',
            headers: { 'X-CSRF-TOKEN': csrfToken() },
        });
        if (resp.ok) setRecenze(prev => prev.filter(r => r.id !== id));
    };

    const handleGenerateAi = async (id) => {
        const resp = await fetch(`/api/social/recenze/${id}/ai`, {
            method: 'POST',
            headers: { 'X-CSRF-TOKEN': csrfToken() },
        });
        if (resp.ok) {
            const updated = await resp.json();
            setRecenze(prev => prev.map(r => r.id === id ? updated : r));
        }
    };

    if (loading) return null;
    if (recenze.length === 0) return null;

    return (
        <div className="mt-5">
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <MessageSquare size={14} style={{ color: SECTION_COLOR }} />
                Recenze k vyřízení
                <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: SECTION_BG, color: SECTION_COLOR }}>
                    {recenze.length}
                </span>
            </h2>
            <div className="space-y-3">
                {recenze.slice(0, 5).map(r => {
                    const isGoogle = r.platforma === 'google';
                    const platColor = isGoogle ? '#34A853' : '#1877F2';
                    const isEditing = editText[r.id] !== undefined;

                    return (
                        <div key={r.id} className="rounded-xl p-3" style={cardStyle}>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs px-1.5 py-0.5 rounded font-medium"
                                    style={{ background: platColor + '20', color: platColor }}>
                                    {isGoogle ? 'Google' : 'Facebook'}
                                </span>
                                {r.hodnoceni && (
                                    <div className="flex items-center gap-0.5">
                                        {[1,2,3,4,5].map(s => (
                                            <Star key={s} size={10} style={{
                                                color: s <= r.hodnoceni ? '#f59e0b' : (isDark ? 'rgba(255,255,255,0.1)' : '#ddd'),
                                                fill: s <= r.hodnoceni ? '#f59e0b' : 'none',
                                            }} />
                                        ))}
                                    </div>
                                )}
                                <span className="text-xs ml-auto" style={{ color: mutedColor }}>
                                    {formatDate(r.publikovano_at)}
                                </span>
                            </div>
                            {r.autor_jmeno && (
                                <p className="text-xs font-medium mb-1" style={{ color: secondaryColor }}>{r.autor_jmeno}</p>
                            )}
                            {r.text && (
                                <p className="text-xs mb-2" style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
                                    „{r.text}"
                                </p>
                            )}
                            {r.ai_navrh && !isEditing && (
                                <div className="rounded-lg p-2 mb-2" style={{ background: isDark ? 'rgba(12,192,223,0.08)' : 'rgba(12,192,223,0.06)', border: '1px solid rgba(12,192,223,0.15)' }}>
                                    <p className="text-xs" style={{ color: '#0CC0DF', fontWeight: 500, marginBottom: 2 }}>AI návrh:</p>
                                    <p className="text-xs" style={{ color: 'var(--text-primary)', lineHeight: 1.4 }}>{r.ai_navrh}</p>
                                </div>
                            )}
                            {isEditing && (
                                <textarea value={editText[r.id]}
                                    onChange={e => setEditText(prev => ({ ...prev, [r.id]: e.target.value }))}
                                    rows={3}
                                    className="w-full rounded-lg px-2 py-1.5 text-xs mb-2 resize-none"
                                    style={{ ...cardStyle, color: 'var(--text-primary)', outline: 'none' }} />
                            )}
                            {isAdmin && (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    {!r.ai_navrh && (
                                        <button onClick={() => handleGenerateAi(r.id)}
                                            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                                            style={{ background: 'rgba(12,192,223,0.12)', color: '#0CC0DF', border: 'none', cursor: 'pointer' }}>
                                            Generovat AI
                                        </button>
                                    )}
                                    {r.ai_navrh && !isEditing && (
                                        <>
                                            <button onClick={() => handleSchval(r.id, r.ai_navrh)}
                                                className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                                                style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e', border: 'none', cursor: 'pointer' }}>
                                                <CheckCircle2 size={11} /> Schválit
                                            </button>
                                            <button onClick={() => setEditText(prev => ({ ...prev, [r.id]: r.ai_navrh }))}
                                                className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                                                style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-elevated)', color: secondaryColor, border: 'none', cursor: 'pointer' }}>
                                                <Edit3 size={11} /> Upravit
                                            </button>
                                        </>
                                    )}
                                    {isEditing && (
                                        <>
                                            <button onClick={() => { handleSchval(r.id, editText[r.id]); setEditText(prev => { const n = {...prev}; delete n[r.id]; return n; }); }}
                                                className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                                                style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e', border: 'none', cursor: 'pointer' }}>
                                                <Send size={11} /> Odeslat
                                            </button>
                                            <button onClick={() => setEditText(prev => { const n = {...prev}; delete n[r.id]; return n; })}
                                                className="px-2 py-1 rounded text-xs font-medium"
                                                style={{ background: 'none', color: mutedColor, border: 'none', cursor: 'pointer' }}>
                                                Zrušit
                                            </button>
                                        </>
                                    )}
                                    <button onClick={() => handlePreskoc(r.id)}
                                        className="flex items-center gap-1 px-2 py-1 rounded text-xs ml-auto"
                                        style={{ background: 'none', color: mutedColor, border: 'none', cursor: 'pointer' }}>
                                        <SkipForward size={11} /> Přeskočit
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
